import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import env from '@/config/env';
import { parseJson } from './infrastructure';
import jwtDecode from 'jwt-decode';
import { STORAGE_KEYS } from '@/utils/infrastructure/auth/sessionConstants';
import { getSessionItem, getSessionToken } from '@/utils/infrastructure/auth/sessionManager';

class AppInsightsService {
  constructor() {
    this.appInsights = null;
    this.initialized = false;
  }

  initialize(connectionString = env.APPLICATIONINSIGHTS_CONNECTION_STRING, config = {}) {
    if (this.initialized) return;

    const shouldTrack = this._shouldTrack();
    if (!shouldTrack || !connectionString) {
      this._initializeNoOp();
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,

          disableAjaxTracking: true,
          disableFetchTracking: true,
          disableExceptionTracking: true,
          disablePageViewTracking: true,
          disableCorrelationHeaders: false,
          disableCookiesUsage: true,
          disableAutoRouteTracking: true,
          disableStackTrace: false,

          autoTrackPageVisitTime: false,
          enableAutoRouteTracking: false,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,

          ...config,
        },
      });

      this.appInsights.loadAppInsights();
      this.initialized = true;
    } catch (error) {
      this._initializeNoOp();
    }
  }

  _sessionProperties() {
    try {
      const token = getSessionToken();
      const { primarysid, companyId, environmentId } = jwtDecode(token?.accessToken);
      return {
        userId: primarysid,
        companyId,
        environmentId,
        appVersion: env.APP_VERSION,
      };
    } catch (e) {}

    try {
      const user = parseJson(getSessionItem(STORAGE_KEYS.AUTH_USER));
      return {
        userId: user?.id,
        companyId: user?.companyId?.id,
        environmentId: user?.defaultEnvironment?.id,
      };
    } catch (e) {}

    return {};
  }

  _shouldTrack() {
    const nodeEnv = process.env.NODE_ENV;
    const reactAppEnv = process.env.REACT_APP_ENV;
    const forceTracking = process.env.FORCE_APP_INSIGHTS === 'true';

    if (forceTracking) return true;
    if (nodeEnv === 'production') return true;
    if (reactAppEnv === 'test') return true;

    return false;
  }

  _initializeNoOp() {
    this.initialized = true;
    this.appInsights = null;
  }

  _truncate(str, maxLength) {
    if (!str) return str;
    return str.length > maxLength ? str.substring(0, maxLength) : str;
  }

  _sanitizeUrl(url, maxLength = 2000) {
    if (!url) return url;
    try {
      const urlObj = new URL(url);
      // Remove query params that might contain PII
      urlObj.search = '';
      return this._truncate(urlObj.toString(), maxLength);
    } catch {
      return this._truncate(url, maxLength);
    }
  }

  isEnabled() {
    return this.initialized && this.appInsights !== null;
  }

  sendTestException() {
    const uniqueId = `TEST_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const testError = new Error(`Test Exception ${uniqueId}`);

    console.log(`[AppInsights] Sending test exception with ID: ${uniqueId}`);
    console.log('[AppInsights] Copy this ID and search for it in Azure Portal');

    const properties = {
      testId: uniqueId,
      testTimestamp: new Date().toISOString(),
      source: 'ManualTest',
    };

    this.trackException(testError, properties, 3);
    if (this.appInsights) this.appInsights.flush();

    return uniqueId;
  }

  trackException(error, errorInfo = {}, severityLevel = 3) {
    if (!this.isEnabled()) return;

    const properties = {
      ...(errorInfo.componentStack && { componentStack: errorInfo.componentStack }),
      ...this._sessionProperties(),
      ...errorInfo,
      trackingTimestamp: new Date().toISOString(),
    };

    this.appInsights.trackException({
      exception: error,
      severityLevel,
      properties,
    });
  }

  trackHttpError({ url, status, method, errorText, responseData }) {
    if (!this.isEnabled()) return;
    try {
      const sanitizedUrl = this._sanitizeUrl(url, 2000);

      const properties = {
        url: sanitizedUrl,
        status,
        method,
        errorText: this._truncate(errorText, 500),
        statusFromResponse: responseData?.status,
        operationName: new URL(sanitizedUrl).pathname,
      };

      this.trackException(
        new Error(`HTTP ${status}: ${errorText || 'Request failed'}`),
        properties,
        3,
      );
    } catch (e) {}
  }

  trackNetworkError({ url, method, error }) {
    if (!this.isEnabled()) return;
    try {
      const properties = {
        url: this._sanitizeUrl(url, 2000),
        method,
        errorType: 'NetworkError',
        errorName: error?.name,
        errorMessage: this._truncate(error?.message, 500),
      };

      this.trackException(error || new Error('Network error'), properties, 3);
    } catch (e) {}
  }

  /**
   * Custom event (no PII). Safe no-op when App Insights is disabled.
   * @param {string} name
   * @param {Record<string, string|number|boolean|undefined>} [properties]
   */
  trackEvent(name, properties = {}) {
    if (!name) return;
    try {
      const safeProps = {};
      Object.keys(properties || {}).forEach((key) => {
        const value = properties[key];
        if (value === undefined || value === null) return;
        safeProps[key] = typeof value === 'string' ? this._truncate(value, 500) : value;
      });

      if (!this.isEnabled()) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.debug('[AppInsights] trackEvent', name, safeProps);
        }
        return;
      }

      this.appInsights.trackEvent({ name, properties: safeProps });
    } catch (e) {}
  }
}

const appInsightsService = new AppInsightsService();

// Add to window for debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.appInsightsService = appInsightsService;
  window.testAppInsights = () => appInsightsService?.sendTestException?.();
}

export default appInsightsService;
